// src/api/productApi.ts
import { http } from "../services/http";

/* ======================
   PAGING
====================== */
export type Paging = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type PagedResult<T> = {
  data: T[];
  pagination: Paging;
};

/* ======================
   DTOs
====================== */
export type ProductListDto = {
  id: number;
  name: string;
  slug: string; // nếu backend không có slug thì map = sku hoặc id
  basePrice: number;
  salePrice?: number | null;
  thumbnail?: string | null;

  // optional extras (không bắt buộc, nhưng giữ lại cho UI dùng nếu có)
  categoryId?: number | null;
  category?: string | null;
  createdAt?: string | null;
};

export type ProductImageDto = {
  id: number;
  imageUrl: string;
};

export type ProductVariantDto = {
  id: number;
  sku: string;
  size: string;
  color: string;
  price: number;
  stockQuantity: number;
};

export type ProductDetailDto = {
  id: number;
  name: string;
  slug: string;
  basePrice: number;
  salePrice?: number | null;
  description?: string | null;
  images: ProductImageDto[];
  variants: ProductVariantDto[];
};

/* ======================
   CLIENT LIST (NEW)
   GET /api/Products/client/list
====================== */
export type FacetDto = {
  colors: string[];
  sizes: string[];
  categories: string[];
};

export type ClientListResponse = {
  products: any[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  facets?: FacetDto;
};

export type ClientListParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  category?: string; // Category.Name
  color?: string;
  size?: string;
  sort?: "new" | "old" | "priceAsc" | "priceDesc";
  inStockOnly?: boolean;
  signal?: AbortSignal;
};

export type ClientListResult = {
  items: ProductListDto[];
  pagination: Paging;
  facets: FacetDto;
};

/* ======================
   API URL + FETCH (public endpoints)
====================== */
function apiUrl() {
  const base = process.env.REACT_APP_API_URL;
  if (!base) throw new Error("REACT_APP_API_URL is not defined");
  return base.replace(/\/+$/, "");
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed: ${res.status}`);
  }

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    // tránh trả undefined gây crash ngầm
    return null as unknown as T;
  }

  return (await res.json()) as T;
}

/* ======================
   CLIENT PAGINATE (khi backend trả array)
====================== */
function clientPaginate<T>(
  all: T[],
  page: number,
  pageSize: number
): PagedResult<T> {
  const total = all.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.max(1, Math.min(totalPages, page));
  const start = (p - 1) * pageSize;
  const data = all.slice(start, start + pageSize);

  return {
    data,
    pagination: { page: p, pageSize, total, totalPages },
  };
}

/* ======================
   NORMALIZE LIST
   - chịu được: ProductId/Name/BasePrice/FinalPrice/ImageUrls/CreatedAt/Category
====================== */
function normalizeProductList(p: any): ProductListDto {
  const id = Number(
    p?.id ?? p?.productId ?? p?.productID ?? p?.ProductId ?? p?.ProductID ?? 0
  );

  const basePrice = Number(
    p?.basePrice ??
      p?.BasePrice ??
      p?.price ??
      p?.Price ??
      p?.displayPrice ??
      0
  );

  const salePrice =
    p?.salePrice != null
      ? Number(p.salePrice)
      : p?.finalPrice != null
      ? Number(p.finalPrice)
      : p?.FinalPrice != null
      ? Number(p.FinalPrice)
      : null;

  const thumbnail =
    p?.thumbnail ??
    p?.thumbnailUrl ??
    p?.imageUrl ??
    (Array.isArray(p?.imageUrls) ? p.imageUrls?.[0] : null) ??
    (Array.isArray(p?.ImageUrls) ? p.ImageUrls?.[0] : null) ??
    (Array.isArray(p?.images) ? p.images?.[0]?.imageUrl : null) ??
    (Array.isArray(p?.Images) ? p.Images?.[0]?.imageUrl : null) ??
    null;

  const sku = String(p?.sku ?? p?.Sku ?? p?.SKU ?? "").trim();

  // ✅ FIX TS5076: không trộn ?? và || không ngoặc
  const slug = String(p?.slug ?? (sku.length ? sku : String(id)));

  const categoryName = (p?.category ??
    p?.Category ??
    p?.categoryName ??
    p?.CategoryName ??
    null) as string | null;

  const categoryIdRaw =
    p?.categoryId ?? p?.CategoryId ?? p?.categoryID ?? p?.CategoryID ?? null;
  const categoryId =
    categoryIdRaw == null || categoryIdRaw === ""
      ? null
      : Number(categoryIdRaw);

  const createdAt = (p?.createdAt ??
    p?.CreatedAt ??
    p?.createAt ??
    p?.CreateAt ??
    null) as string | null;

  return {
    id,
    name: String(p?.name ?? p?.Name ?? p?.productName ?? ""),
    slug,
    basePrice,
    salePrice,
    thumbnail,
    categoryId,
    category: categoryName,
    createdAt,
  };
}

function normalizeProductListArray(list: any): ProductListDto[] {
  const arr: any[] = Array.isArray(list) ? list : [];
  return arr.map(normalizeProductList);
}

function normalizePagedProductList(raw: any): PagedResult<ProductListDto> {
  const list = raw?.data ?? raw?.products ?? raw?.items ?? [];
  const pagination = raw?.pagination;

  const data = normalizeProductListArray(list);

  if (pagination && typeof pagination === "object") {
    return {
      data,
      pagination: {
        page: Number(pagination.page ?? 1),
        pageSize: Number(pagination.pageSize ?? data.length ?? 12),
        total: Number(pagination.total ?? data.length ?? 0),
        totalPages: Number(pagination.totalPages ?? 1),
      },
    };
  }

  return {
    data,
    pagination: {
      page: 1,
      pageSize: data.length,
      total: data.length,
      totalPages: 1,
    },
  };
}

/* ======================
   NORMALIZE DETAIL
====================== */
function normalizeProductDetail(p: any): ProductDetailDto {
  const id = Number(p?.id ?? p?.productId ?? p?.ProductId ?? 0);

  const basePrice = Number(
    p?.basePrice ?? p?.BasePrice ?? p?.price ?? p?.Price ?? p?.displayPrice ?? 0
  );

  const salePrice =
    p?.salePrice != null
      ? Number(p.salePrice)
      : p?.finalPrice != null
      ? Number(p.finalPrice)
      : p?.FinalPrice != null
      ? Number(p.FinalPrice)
      : null;

  const images: ProductImageDto[] = Array.isArray(p?.images)
    ? p.images.map((img: any, idx: number) => ({
        id: Number(img?.id ?? img?.imageId ?? img?.ImageId ?? idx + 1),
        imageUrl: String(img?.imageUrl ?? img?.url ?? img?.src ?? ""),
      }))
    : Array.isArray(p?.imageUrls)
    ? p.imageUrls.map((url: any, idx: number) => ({
        id: idx + 1,
        imageUrl: String(url),
      }))
    : Array.isArray(p?.ImageUrls)
    ? p.ImageUrls.map((url: any, idx: number) => ({
        id: idx + 1,
        imageUrl: String(url),
      }))
    : [];

  let variants: ProductVariantDto[] = [];

  if (Array.isArray(p?.variants)) {
    variants = p.variants.map((v: any) => ({
      id: Number(v?.id ?? v?.variantId ?? v?.VariantId ?? 0),
      sku: String(v?.sku ?? v?.Sku ?? ""),
      size: String(v?.size ?? v?.sizeName ?? v?.Size ?? ""),
      color: String(v?.color ?? v?.colorName ?? v?.Color ?? ""),
      price: Number(v?.price ?? v?.Price ?? basePrice ?? 0),
      stockQuantity: Number(v?.stockQuantity ?? v?.stock ?? v?.Stock ?? 0),
    }));
  } else if (Array.isArray(p?.variations)) {
    variants = p.variations.flatMap((vr: any, colorIdx: number) => {
      const color = String(vr?.color ?? vr?.Color ?? "");
      const sizes = Array.isArray(vr?.sizes) ? vr.sizes : [];

      return sizes.map((s: any, sizeIdx: number) => {
        const size = String(s?.size ?? s?.Size ?? s?.sizeName ?? "");
        const price = Number(s?.price ?? s?.Price ?? salePrice ?? basePrice ?? 0);
        const stock = Number(
          s?.stock ??
            s?.stockQuantity ??
            s?.Stock ??
            s?.StockQuantity ??
            0
        );

        const fakeId = id * 1000 + colorIdx * 100 + sizeIdx;

        return {
          id: fakeId,
          sku: String(p?.sku ?? p?.Sku ?? p?.name ?? `P${id}`),
          size,
          color,
          price,
          stockQuantity: stock,
        };
      });
    });
  }

  const slug = String(p?.slug ?? p?.sku ?? p?.Sku ?? id);

  return {
    id,
    name: String(p?.name ?? p?.Name ?? ""),
    slug,
    basePrice,
    salePrice,
    description: (p?.description ??
      p?.Description ??
      p?.shortDescription ??
      p?.ShortDescription ??
      null) as any,
    images,
    variants,
  };
}

/* ======================
   HOME SECTIONS (Swagger)
   (public => giữ fetchJson)
====================== */
export async function getNewProducts(signal?: AbortSignal) {
  const raw = await fetchJson<any[]>(`${apiUrl()}/api/Products/new`, { signal });
  return normalizeProductListArray(raw);
}

export async function getBestSellingProducts(signal?: AbortSignal) {
  const raw = await fetchJson<any[]>(
    `${apiUrl()}/api/Products/bestselling`,
    { signal }
  );
  return normalizeProductListArray(raw);
}

export async function getDiscountedProducts(signal?: AbortSignal) {
  const raw = await fetchJson<any[]>(
    `${apiUrl()}/api/Products/discounted`,
    { signal }
  );
  return normalizeProductListArray(raw);
}

/* ======================
   PRODUCT DETAIL (CLIENT)
   GET /api/Products/client/{id}
   (auth => dùng http)
====================== */
export async function getProductClientById(id: number, signal?: AbortSignal) {
  const res = await http.get<any>(`/api/Products/client/${id}`, { signal });
  return normalizeProductDetail(res.data);
}

/* ======================
   NEW: CLIENT LIST (SERVER PAGING + FILTER + FACETS)
   GET /api/Products/client/list
====================== */
export async function getClientProductList(
  params: ClientListParams = {}
): Promise<ClientListResult> {
  const qs = new URLSearchParams();

  qs.set("page", String(params.page ?? 1));
  qs.set("pageSize", String(params.pageSize ?? 12));

  const q = params.q?.trim();
  const category = params.category?.trim();
  const color = params.color?.trim();
  const size = params.size?.trim();

  if (q) qs.set("q", q);
  if (category) qs.set("category", category);
  if (color) qs.set("color", color);
  if (size) qs.set("size", size);

  qs.set("sort", params.sort ?? "new");
  qs.set("inStockOnly", String(!!params.inStockOnly));

  const res = await http.get<ClientListResponse>(
    `/api/Products/client/list?${qs.toString()}`,
    { signal: params.signal }
  );

  const raw: any = res.data ?? {};

  const items = normalizeProductListArray(raw?.products ?? []);

  const pgRaw = raw?.pagination ?? {};
  const pagination: Paging = {
    page: Number(pgRaw.page ?? params.page ?? 1),
    pageSize: Number(pgRaw.pageSize ?? params.pageSize ?? 12),
    total: Number(pgRaw.total ?? items.length ?? 0),
    totalPages: Number(pgRaw.totalPages ?? 1),
  };

  const facetsRaw = raw?.facets ?? {};
  const facets: FacetDto = {
    colors: Array.isArray(facetsRaw.colors) ? facetsRaw.colors : [],
    sizes: Array.isArray(facetsRaw.sizes) ? facetsRaw.sizes : [],
    categories: Array.isArray(facetsRaw.categories) ? facetsRaw.categories : [],
  };

  return { items, pagination, facets };
}

/* ======================
   FILTER: by-name (Swagger)
   GET /api/Filter/by-name?name=...
====================== */
export async function filterByName(
  name: string,
  signal?: AbortSignal
): Promise<ProductListDto[]> {
  const qs = new URLSearchParams();
  qs.set("name", name);

  const raw: any = await fetchJson<any>(
    `${apiUrl()}/api/Filter/by-name?${qs.toString()}`,
    { signal }
  );

  const arr: any[] = Array.isArray(raw) ? raw : raw?.data ?? raw?.products ?? [];
  return normalizeProductListArray(arr);
}

/* ======================
   FILTER APIs (optional)
====================== */
export function filterByTags(tags: string[], signal?: AbortSignal) {
  const qs = new URLSearchParams();
  for (const t of tags) qs.append("tags", t);
  return fetchJson<any[]>(
    `${apiUrl()}/api/Filter/by-tags?${qs.toString()}`,
    { signal }
  );
}

export function filterByColor(color: string, signal?: AbortSignal) {
  const qs = new URLSearchParams();
  qs.set("color", color);
  return fetchJson<any[]>(
    `${apiUrl()}/api/Filter/by-color?${qs.toString()}`,
    { signal }
  );
}

export function filterBySize(size: string, signal?: AbortSignal) {
  const qs = new URLSearchParams();
  qs.set("size", size);
  return fetchJson<any[]>(
    `${apiUrl()}/api/Filter/by-size?${qs.toString()}`,
    { signal }
  );
}

export function filterByPrice(
  params: { minPrice?: number; maxPrice?: number },
  signal?: AbortSignal
) {
  const qs = new URLSearchParams();
  if (params.minPrice != null) qs.set("minPrice", String(params.minPrice));
  if (params.maxPrice != null) qs.set("maxPrice", String(params.maxPrice));
  return fetchJson<any[]>(
    `${apiUrl()}/api/Filter/by-price?${qs.toString()}`,
    { signal }
  );
}

/* ======================
   MAIN: GET PRODUCTS (LIST)
   (giữ lại cho những page cũ đang gọi)
====================== */
export async function getProducts(params: {
  keyword?: string;
  collectionId?: number;
  collectionSlug?: string;
  page?: number;
  pageSize?: number;
  sort?: string; // "order" | "new" | ...
}): Promise<PagedResult<ProductListDto>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 12;
  const sort = params.sort ?? "order";

  // 1) Search
  if (params.keyword && params.keyword.trim().length > 0) {
    const list = await filterByName(params.keyword.trim());
    return clientPaginate(list, page, pageSize);
  }

  // 2) By collectionId
  if (typeof params.collectionId === "number") {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    qs.set("sort", sort);

    const url = `${apiUrl()}/api/Collections/${params.collectionId}/products?${qs.toString()}`;
    const raw: any = await fetchJson<any>(url);
    return normalizePagedProductList(raw);
  }

  // 3) By collectionSlug
  if (params.collectionSlug) {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    qs.set("sort", sort);

    const url = `${apiUrl()}/api/Collections/${encodeURIComponent(
      params.collectionSlug
    )}?${qs.toString()}`;

    const raw: any = await fetchJson<any>(url);

    if (raw?.pagination) return normalizePagedProductList(raw);

    if (Array.isArray(raw)) {
      return clientPaginate(normalizeProductListArray(raw), page, pageSize);
    }

    if (raw?.products) {
      const normalized = normalizeProductListArray(raw.products);
      if (raw?.pagination) {
        return normalizePagedProductList({
          data: normalized,
          pagination: raw.pagination,
        });
      }
      return clientPaginate(normalized, page, pageSize);
    }

    return {
      data: [],
      pagination: { page: 1, pageSize, total: 0, totalPages: 1 },
    };
  }

  // fallback
  return {
    data: [],
    pagination: { page: 1, pageSize, total: 0, totalPages: 1 },
  };
}
